import { FC, useMemo, useRef } from 'react';

import { useDrag, useDrop } from 'react-dnd';

import {
  IPolicyRule,
  IPolicyRuleCondition,
  IPolicyRuleConditionItem,
  IPolicyRuleTagComparator,
  useRules,
} from '@entities/policy';
import ModalConfirm from '@features/modal-confirm';
import ClearIcon from '@mui/icons-material/Clear';
import DragIndicatorIcon from '@mui/icons-material/DragIndicator';
import EditIcon from '@mui/icons-material/Edit';
import {
  Box,
  Chip,
  IconButton,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Typography,
} from '@mui/material';
import LoadingSpinner from '@shared/components/LoadingSpinner';
import { useModal } from '@shared/components/Modal';

interface IProps {
  policyId: string;
  rules?: IPolicyRule[];
  onReorderRules?: (ids: string[]) => void;
  onOpenRuleForm: () => void;
  onEditRule?: (rule: IPolicyRule) => void;
  onDeleteRule?: (rule: IPolicyRule) => void;
  localOrderIds: string[] | null;
  onLocalReorder: (ids: string[]) => void;
}

const PolicyRulesTable: FC<IProps> = ({
  policyId,
  rules = [],
  onReorderRules,
  onOpenRuleForm,
  onEditRule,
  onDeleteRule,
  localOrderIds,
  onLocalReorder,
}) => {
  const { rules: fetchedRules, isLoading, isFetching, isError, error } = useRules(policyId);
  const activeRules = fetchedRules.length ? fetchedRules : rules;

  const sortedRules = useMemo(() => {
    if (!localOrderIds) {
      return [...activeRules].sort((a, b) => a.priority - b.priority);
    }
    return reorderRulesByIds(activeRules, localOrderIds);
  }, [activeRules, localOrderIds]);

  const rulesRef = useRef(sortedRules);
  rulesRef.current = sortedRules;
  const { openModal, closeModal } = useModal();

  const renderContent = () => {
    if (isLoading || isFetching) {
      return (
        <Box
          sx={{
            position: 'relative',
            minHeight: 100,
            py: 2,
            display: 'flex',
            justifyContent: 'center',
          }}
        >
          <LoadingSpinner />
        </Box>
      );
    }

    if (isError) {
      return (
        <Typography variant='body2' color='error'>
          {String(error)}
        </Typography>
      );
    }

    if (!activeRules.length) {
      return (
        <Typography variant='body2' color='text.secondary'>
          No rules yet, you can add them now.
        </Typography>
      );
    }

    return (
      <Table size='small' className='policy-rules-table'>
        <TableHead>
          <TableRow>
            <TableCell align='left'>Priority</TableCell>
            <TableCell align='left'>Name</TableCell>
            <TableCell align='left'>Conditions</TableCell>
            <TableCell align='left'>Selection</TableCell>
            <TableCell align='left' width='100px'>
              Limits
            </TableCell>
            <TableCell align='left'>Strategy</TableCell>
            <TableCell align='center'>Actions</TableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          {sortedRules.map((rule, index) => (
            <RuleDraggableRow
              key={rule.id}
              rule={rule}
              index={index}
              rules={rulesRef.current}
              onConfirmReorder={onReorderRules}
              openModal={openModal}
              closeModal={closeModal}
              onLocalReorder={onLocalReorder}
              onEditRule={onEditRule}
              onDeleteRule={onDeleteRule}
            />
          ))}
          {sortedRules.length > 0 && (
            <RuleDropZoneRow
              columnCount={7}
              rules={sortedRules}
              onConfirmReorder={onReorderRules}
              onLocalReorder={onLocalReorder}
              openModal={openModal}
              closeModal={closeModal}
            />
          )}
        </TableBody>
      </Table>
    );
  };

  return (
    <Box sx={{ my: 1 }}>
      <Stack direction='row' alignItems='center' justifyContent='space-between' sx={{ mb: 1 }}>
        <Typography variant='body1' fontWeight={700}>
          Rules (Limits)
        </Typography>
        <button onClick={onOpenRuleForm}>New rule</button>
      </Stack>
      {renderContent()}
    </Box>
  );
};

export default PolicyRulesTable;

type RuleDragItem = { id: string; index: number; type: string };

interface IRuleDropZoneRowProps {
  rules: IPolicyRule[];
  columnCount: number;
  onConfirmReorder?: (ids: string[]) => void;
  onLocalReorder: (ids: string[]) => void;
  openModal: ReturnType<typeof useModal>['openModal'];
  closeModal: ReturnType<typeof useModal>['closeModal'];
}

const RuleDropZoneRow: FC<IRuleDropZoneRowProps> = ({
  rules,
  columnCount,
  onConfirmReorder,
  onLocalReorder,
  openModal,
  closeModal,
}) => {
  const reorderLocalByIndex = (fromIndex: number, toIndex: number) => {
    const next = [...rules];
    if (fromIndex < 0 || fromIndex >= next.length) return next.map(r => r.id);
    const [removed] = next.splice(fromIndex, 1);
    const nextIndex = Math.max(0, Math.min(toIndex, next.length));
    next.splice(nextIndex, 0, removed);
    return next.map((r, idx) => ({ ...r, priority: idx + 1 })).map(r => r.id);
  };

  const moveToEnd = (dragIndex: number) => {
    if (!onConfirmReorder) return;
    const ids = reorderLocalByIndex(dragIndex, rules.length);
    openModal(
      <ModalConfirm
        title='Change rule priority'
        message='Do you want to update rule priority?'
        onConfirm={() => {
          onLocalReorder(ids);
          onConfirmReorder(ids);
          closeModal();
        }}
        onCancel={closeModal}
      />,
    );
  };

  const [{ isOverEnd }, dropEnd] = useDrop<RuleDragItem, void, { isOverEnd: boolean }>({
    accept: 'POLICY_RULE',
    drop: item => moveToEnd(item.index),
    collect: monitor => ({
      isOverEnd: monitor.isOver({ shallow: true }),
    }),
  });

  return (
    <TableRow
      ref={dropEnd}
      className={`policy-rules-drop-zone-row ${isOverEnd ? 'rule-drop-target-top' : ''}`}
    >
      <TableCell colSpan={columnCount} />
    </TableRow>
  );
};

interface IRuleRowProps {
  rule: IPolicyRule;
  index: number;
  rules: IPolicyRule[];
  onConfirmReorder?: (ids: string[]) => void;
  openModal: ReturnType<typeof useModal>['openModal'];
  closeModal: ReturnType<typeof useModal>['closeModal'];
  onLocalReorder: (ids: string[]) => void;
  onEditRule: (rule: IPolicyRule) => void;
  onDeleteRule: (rule: IPolicyRule) => void;
}

const RuleDraggableRow: FC<IRuleRowProps> = ({
  rule,
  index,
  rules,
  onConfirmReorder,
  openModal,
  closeModal,
  onLocalReorder,
  onEditRule,
  onDeleteRule,
}) => {
  const ref = useRef<HTMLTableRowElement>(null);

  const reorderLocalByIndex = (fromIndex: number, toIndex: number) => {
    const next = [...rules];
    if (fromIndex < 0 || fromIndex >= next.length) return next.map(r => r.id);
    const [removed] = next.splice(fromIndex, 1);
    const nextIndex = Math.max(0, Math.min(toIndex, next.length));
    next.splice(nextIndex, 0, removed);
    return next.map((r, idx) => ({ ...r, priority: idx + 1 })).map(r => r.id);
  };

  const moveAndConfirm = (dragIndex: number, hoverIndex: number, position: 'top' | 'bottom') => {
    if (dragIndex === hoverIndex || !onConfirmReorder) return;
    let toIndex = hoverIndex;
    if (position === 'bottom') {
      toIndex += 1;
    }
    if (dragIndex < toIndex) {
      toIndex -= 1;
    }
    const ids = reorderLocalByIndex(dragIndex, toIndex);
    openModal(
      <ModalConfirm
        title='Change rule priority'
        message='Do you want to update rule priority?'
        onConfirm={() => {
          onLocalReorder(ids);
          onConfirmReorder(ids);
          closeModal();
        }}
        onCancel={closeModal}
      />,
    );
  };

  const dropPositionRef = useRef<'top' | 'bottom' | null>(null);

  const [{ isOver, draggedId, dropPosition }, drop] = useDrop<
    RuleDragItem,
    void,
    { isOver: boolean; draggedId: string | null; dropPosition: 'top' | 'bottom' | null }
  >({
    accept: 'POLICY_RULE',
    drop: item => {
      if (!ref.current || item.index === index) return;
      moveAndConfirm(item.index, index, dropPositionRef.current ?? 'top');
    },
    hover: (_item, monitor) => {
      if (!ref.current) return;
      const clientOffset = monitor.getClientOffset();
      if (!clientOffset) return;
      const rect = ref.current.getBoundingClientRect();
      const middleY = (rect.bottom - rect.top) / 2;
      const hoverClientY = clientOffset.y - rect.top;
      dropPositionRef.current = hoverClientY < middleY ? 'top' : 'bottom';
    },
    collect: monitor => {
      const item = monitor.getItem() as RuleDragItem | null;
      if (!monitor.isOver({ shallow: true })) {
        dropPositionRef.current = null;
      }
      return {
        isOver: monitor.isOver({ shallow: true }),
        draggedId: item?.id ?? null,
        dropPosition: dropPositionRef.current,
      };
    },
  });

  const [{ isDragging }, drag] = useDrag({
    type: 'POLICY_RULE',
    item: { id: rule.id, index },
    collect: monitor => ({
      isDragging: monitor.isDragging(),
    }),
  });

  drag(drop(ref));

  const isDropTarget = isOver && draggedId !== rule.id;
  const dropClass =
    isDropTarget && dropPosition
      ? dropPosition === 'top'
        ? 'rule-drop-target-top'
        : 'rule-drop-target-bottom'
      : '';

  return (
    <TableRow
      ref={ref}
      className={dropClass}
      style={{ opacity: isDragging ? 0.4 : 1, cursor: 'grab' }}
    >
      <TableCell align='left' sx={{ verticalAlign: 'top', width: 120 }}>
        <Stack direction='row' spacing={0.5} alignItems='center'>
          <DragIndicatorIcon sx={{ color: 'rgba(0, 0, 0, 0.5)' }} fontSize='small' />
          <Chip size='small' variant='outlined' label={`${rule.priority}`} />
        </Stack>
      </TableCell>
      <TableCell align='left' sx={{ verticalAlign: 'top', width: 120 }}>
        <Typography variant='body2' fontWeight={600}>
          {rule.name}
        </Typography>
        {rule.description && (
          <Typography variant='caption' color='text.secondary'>
            {rule.description}
          </Typography>
        )}
      </TableCell>
      <TableCell align='left' sx={{ verticalAlign: 'top', minWidth: 180 }}>
        {renderConditionCells(rule.conditions)}
      </TableCell>
      <TableCell align='left' sx={{ verticalAlign: 'top' }}>
        {renderConditionCells(rule.selections)}
      </TableCell>
      <TableCell align='left' sx={{ verticalAlign: 'top' }}>
        <Typography variant='body2' color='text.primary'>
          {rule.min ?? 0} - {rule.max ?? 0}
        </Typography>
      </TableCell>
      <TableCell align='left' sx={{ verticalAlign: 'top' }}>
        <Typography variant='body2' color='primary'>
          {(rule.sort_strategy ?? 'random').toUpperCase()}
        </Typography>
      </TableCell>
      <TableCell align='left' sx={{ verticalAlign: 'top' }}>
        <Stack direction='row' spacing={1} justifyContent='center'>
          <IconButton size='small' disabled={!onEditRule} onClick={() => onEditRule?.(rule)}>
            <EditIcon sx={{ color: '#ff9800' }} fontSize='small' />
          </IconButton>
          <IconButton size='small' disabled={!onDeleteRule} onClick={() => onDeleteRule?.(rule)}>
            <ClearIcon sx={{ color: 'red' }} fontSize='small' />
          </IconButton>
        </Stack>
      </TableCell>
    </TableRow>
  );
};

type NormalizedRuleCondition = {
  key: string;
  type: 'multi-select' | 'digit-compare' | 'tag-digit-compare';
  rawValue: string[] | IPolicyRuleTagComparator;
};

const normalizeRuleConditions = (
  conditions?: IPolicyRuleCondition | IPolicyRuleConditionItem[],
): NormalizedRuleCondition[] => {
  if (!conditions) return [];
  if (Array.isArray(conditions)) {
    return conditions.map(item => ({
      key: item.name,
      type: item.type,
      rawValue: item.rawValue,
    }));
  }
  return Object.entries(conditions).map(([key, value]) => ({
    key,
    type: Array.isArray(value)
      ? 'multi-select'
      : Array.isArray(value.tags)
        ? 'tag-digit-compare'
        : 'digit-compare',
    rawValue: value,
  }));
};

const renderConditionCells = (conditions?: IPolicyRuleCondition | IPolicyRuleConditionItem[]) => {
  const normalized = normalizeRuleConditions(conditions);
  if (!normalized.length) {
    return (
      <Typography variant='caption' color='text.secondary'>
        —
      </Typography>
    );
  }

  return (
    <Stack alignItems='flex-start' spacing={1}>
      {normalized.map(condition => {
        const label = formatRuleKey(condition.key);
        if (Array.isArray(condition.rawValue)) {
          return (
            <Stack alignItems='flex-start' key={condition.key} spacing={0.5}>
              <Typography variant='caption' color='text.secondary'>
                {label}
              </Typography>
              {condition.rawValue.length ? (
                <Stack direction='row' spacing={0.5} flexWrap='wrap'>
                  {condition.rawValue.map(item => (
                    <Chip sx={{ margin: '2px !important' }} key={item} size='small' label={item} />
                  ))}
                </Stack>
              ) : (
                <Typography variant='body2' fontWeight={600} color='text.primary'>
                  Any
                </Typography>
              )}
            </Stack>
          );
        }

        const { tags, ...comparator } = condition.rawValue;
        const operatorEntry = Object.entries(comparator)[0];
        const hasTags = condition.type === 'tag-digit-compare' || Array.isArray(tags);

        return (
          <Stack key={condition.key} spacing={0.5} alignItems='flex-start'>
            <Typography variant='caption' color='text.secondary'>
              {label}
            </Typography>
            <Typography variant='body2' fontWeight={600} color='text.primary'>
              {operatorEntry ? `${formatOperator(operatorEntry[0])} ${operatorEntry[1]}` : '—'}
            </Typography>
            {hasTags &&
              (tags?.length ? (
                <Stack direction='row' spacing={0.5} flexWrap='wrap'>
                  {tags.map(tag => (
                    <Chip sx={{ margin: '2px !important' }} key={tag} size='small' label={tag} />
                  ))}
                </Stack>
              ) : (
                <Typography variant='caption' color='text.secondary'>
                  Any tag
                </Typography>
              ))}
          </Stack>
        );
      })}
    </Stack>
  );
};

const formatRuleKey = (key: string) =>
  key.replace(/_/g, ' ').replace(/\b\w/g, char => char.toUpperCase());

const formatOperator = (op: string) => {
  switch (op) {
    case 'eq':
      return '=';
    case 'gt':
      return '>';
    case 'gte':
      return '≥';
    case 'lt':
      return '<';
    case 'lte':
      return '≤';
    default:
      return op;
  }
};

const reorderRulesByIds = (rules: IPolicyRule[], ids: string[]) => {
  const map = new Map(rules.map(rule => [rule.id, rule]));
  const reordered: IPolicyRule[] = [];
  ids.forEach((id, idx) => {
    const rule = map.get(id);
    if (!rule) return;
    reordered.push({ ...rule, priority: idx + 1 });
    map.delete(id);
  });
  map.forEach(rule => reordered.push({ ...rule, priority: reordered.length + 1 }));
  return reordered;
};
